import React, {
  useState,
  useEffect,
} from "react";

import {
  Link,
  useLocation,
} from "react-router-dom";

import { motion } from "framer-motion";

import {
  ShoppingBag,
  List,
  X,
} from "@phosphor-icons/react";

import Logo from "./Logo";

import { useCart } from "@/context/CartContext";

/* =========================================================
   NAVIGATION LINKS
========================================================= */

const links = [
  {
    to: "/",
    label: "Home",
  },
  {
    to: "/shop",
    label: "Shop",
  },
  {
    to: "/#schools",
    label: "Schools",
  },
  {
    to: "/#about",
    label: "Atelier",
  },
  {
    to: "/#contact",
    label: "Contact",
  },
];

/* =========================================================
   NAVBAR
========================================================= */

export const Navbar = () => {
  const {
    count,
    setOpen,
  } = useCart();

  const [scrolled, setScrolled] =
    useState(false);

  const [menu, setMenu] =
    useState(false);

  const location =
    useLocation();

  /* =======================================================
     SCROLL
  ======================================================= */

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(
        window.scrollY > 40
      );
    };

    window.addEventListener(
      "scroll",
      handleScroll
    );

    return () => {
      window.removeEventListener(
        "scroll",
        handleScroll
      );
    };
  }, []);

  /* =======================================================
     CLOSE MOBILE MENU ON ROUTE CHANGE
  ======================================================= */

  useEffect(() => {
    setMenu(false);
  }, [location]);

  /* =======================================================
     OPEN CART
  ======================================================= */

  const handleCartClick = () => {
    /*
     * This updates CartContext.open.
     *
     * CartDrawer also reads CartContext.open.
     *
     * Therefore the drawer will now actually appear.
     */
    setOpen(true);
  };

  return (
    <motion.header
      initial={{
        y: -80,
      }}
      animate={{
        y: 0,
      }}
      transition={{
        duration: 0.8,
        ease: [
          0.16,
          1,
          0.3,
          1,
        ],
      }}
      className={`
        fixed
        top-0
        left-0
        right-0
        z-50
        transition-colors
        duration-300
        border-b
        ${
          scrolled
            ? "backdrop-blur-xl bg-primary/90 border-foreground/10"
            : "backdrop-blur-md bg-primary/75 border-transparent"
        }
      `}
      data-testid="main-navbar"
    >

      <div
        className="
          max-w-[1400px]
          mx-auto
          px-6
          md:px-12
          h-20
          flex
          items-center
          justify-between
        "
      >

        {/* LOGO */}

        <Link
          to="/"
          data-testid="nav-home-link"
        >
          <Logo />
        </Link>

        {/* DESKTOP NAVIGATION */}

        <nav
          className="
            hidden
            md:flex
            items-center
            gap-9
          "
        >

          {links.map((link) => (

            <a
              key={link.label}
              href={link.to}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              className="
                text-sm
                tracking-wide
                text-foreground/80
                hover:text-accent
                transition-colors
                relative
                group
              "
            >

              {link.label}

              <span
                className="
                  absolute
                  -bottom-1
                  left-0
                  w-0
                  h-px
                  bg-accent
                  transition-all
                  duration-300
                  group-hover:w-full
                "
              />

            </a>

          ))}

        </nav>

        {/* RIGHT SIDE */}

        <div
          className="
            flex
            items-center
            gap-4
          "
        >

          {/* CART */}

          <button
            type="button"
            onClick={
              handleCartClick
            }
            data-testid="nav-cart-button"
            className="
              relative
              p-2
              hover:text-accent
              transition-colors
            "
            aria-label="Open shopping cart"
          >

            <ShoppingBag
              size={22}
              weight="light"
            />

            {count > 0 && (

              <span
                className="
                  absolute
                  -top-0.5
                  -right-0.5
                  bg-accent
                  text-accent-foreground
                  text-[10px]
                  w-4
                  h-4
                  rounded-full
                  flex
                  items-center
                  justify-center
                  font-medium
                "
                data-testid="cart-count-badge"
              >
                {count}
              </span>

            )}

          </button>

          {/* MOBILE MENU */}

          <button
            type="button"
            className="
              md:hidden
              p-2
            "
            onClick={() =>
              setMenu(!menu)
            }
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >

            {menu ? (
              <X size={24} />
            ) : (
              <List size={24} />
            )}

          </button>

        </div>

      </div>

      {/* MOBILE NAVIGATION */}

      {menu && (

        <motion.nav
          initial={{
            opacity: 0,
            height: 0,
          }}
          animate={{
            opacity: 1,
            height: "auto",
          }}
          className="
            md:hidden
            bg-background
            border-t
            border-border
            px-6
            py-4
            flex
            flex-col
            gap-4
          "
        >

          {links.map((link) => (

            <a
              key={link.label}
              href={link.to}
              className="
                text-base
                py-1
              "
              data-testid={`mobile-nav-${link.label.toLowerCase()}`}
            >
              {link.label}
            </a>

          ))}

        </motion.nav>

      )}

    </motion.header>
  );
};

export default Navbar;
